import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ReactNode, useState } from "react";
import { GroupSelect } from "../filter/GroupSelect";
import { useGeotabClient } from "../GeotabClientContext";
import LanguageToggle from "../LanguageToggle";
import { LoginPage } from "../login/LoginPage";
import { DocsDrawer } from "./DocsDrawer";
import SessionSwitcher from "./SessionSwitcher";

interface Props {
  title: string;
  content: ReactNode | ReactNode[];
  handleHomeClick?: () => void;
  docs?: ReactNode;
  /**
   * The button that opens the list of all pages, top left.
   *
   * Passed in rather than built here: only the home page knows what pages there are and how this
   * reader has arranged them, and only it can switch between them.
   */
  appMenuButton?: ReactNode;
}

export default function PageHeader({
  title,
  content,
  handleHomeClick,
  docs,
  appMenuButton,
}: Props) {
  const { geotabView } = useGeotabClient();
  const [loginPage, setLoginPage] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1400));

  const handleDrawerToggle = () => {
    setDrawerOpen(prev => !prev);
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        minWidth: 250,
        backgroundColor: theme.palette.primary.main,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon color="secondary" />
        </IconButton>
      </Box>

      {!geotabView && (
        <>
          <SessionSwitcher isMobile={isMobile} />
          <Button
            fullWidth
            startIcon={loginPage ? <HomeIcon /> : <AddCircleIcon />}
            onClick={() => setLoginPage(!loginPage)}
            variant="contained"
          >
            {loginPage ? "Back to App" : "Login"}
          </Button>

          {!loginPage && handleHomeClick && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleHomeClick}
            >
              {"Go Home"}
            </Button>
          )}
        </>
      )}

      <GroupSelect />
      <LanguageToggle />
    </Box>
  );
  return (
    <Stack sx={{ height: "100%", overflow: "auto" }}>
      <AppBar position="sticky" sx={{ height: "50px", zIndex: theme.zIndex.appBar }}>
        <Toolbar variant="dense">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Title and menu toggle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {appMenuButton}
              {isMobile && (
                <IconButton color="inherit" onClick={handleDrawerToggle}>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="h6" noWrap>
                {title}
              </Typography>
              {docs && (
                <IconButton color="inherit" size="small" onClick={() => setDocsOpen(true)}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              )}
              <LanguageToggle />
            </Box>

            {/* Desktop Center + Right Elements */}
            {!isMobile && !geotabView && (
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <SessionSwitcher />
                <Button variant="contained" onClick={() => setLoginPage(!loginPage)}>
                  {loginPage ? <HomeIcon /> : <AddCircleIcon />}
                </Button>
                {!loginPage && handleHomeClick && (
                  <Button sx={{ ml: 2 }} variant="contained" onClick={handleHomeClick}>
                    <HomeIcon />
                  </Button>
                )}
              </Box>
            )}

            {!isMobile && <GroupSelect />}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerToggle}>
        {drawerContent}
      </Drawer>

      {docs && (
        <DocsDrawer title={title} open={docsOpen} onClose={() => setDocsOpen(false)}>
          {docs}
        </DocsDrawer>
      )}

      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        {loginPage ? <LoginPage onLoggedIn={() => setLoginPage(false)} /> : content}
      </Box>
    </Stack>
  );
}
